import {
  DC_Domain_Models_Customers_RelocationReason,
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  Products_Relocations_DiscontinueProductType,
  RequestModels_Products_FineCalculation_CustomerProductFineCalculationRequestModel,
  RequestModels_Products_FineCalculation_DiscontinueConfirmation,
  RequestModels_Products_Order_ProductOrderPersonal,
  RequestModels_Products_Relocations_RelocationCancellationRequestModel,
  ResponseDataSystem_String,
  ResponseModels_Customers_Relocations_RelocationIntakeResponse,
  ResponseModels_Products_FineCalculation_CustomerProductFineCalculationResponseModel,
  ResponseModels_Products_FineCalculation_DiscontinueIntakeModel,
  ResponseModels_Products_Maintenance_ServiceProductMaintenanceDetailsResponseModel,
  ResponseModels_Products_Offers_PublicOffer,
  ResponseModels_Products_Offers_V3_PersonalOfferResponseModel,
  ResponseModels_Products_OrderStatus_Order,
  ResponseModels_Products_OrderStatus_TrackAndTrace_TrackAndTraceOrderResponseModel,
  ResponseModels_Products_Orders_CreateOrderResponseLocal,
  ResponseModels_Products_ProductOverview_ProductOverviewResponseModel,
  ResponseModels_Products_ProductOverview_ProductsOverviewResponseModel,
  ResponseModels_Products_ProductsStatusOverviewResponseModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type GetProductsForAccountV2 = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  includeProductRates?: boolean;
  active?: boolean;
  includeInactive?: boolean;
};
/**
 * Gets the products for account v2.
 *
 * @returns ResponseModels_Products_ProductOverview_ProductsOverviewResponseModel Success
 */
export function getProductsForAccountV2(
  { businessUnit, label, customerId, accountId, includeProductRates, active, includeInactive }: GetProductsForAccountV2,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_ProductOverview_ProductsOverviewResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/v2/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/products`,
      query: { includeProductRates, active, includeInactive },
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetHeatProductsForAccount = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  includeProductRates?: boolean;
};
/**
 * Gets the heat products for account.
 *
 * @returns ResponseModels_Products_ProductOverview_ProductOverviewResponseModel Success
 */
export function getHeatProductsForAccount(
  { businessUnit, label, customerId, accountId, includeProductRates }: GetHeatProductsForAccount,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_ProductOverview_ProductOverviewResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/products/heat`,
      query: { includeProductRates },
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetOrderStatus = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
};
/**
 * Get the customer profile including any open orders
 *
 * @returns ResponseModels_Products_OrderStatus_Order Success
 */
export function getOrderStatus(
  { businessUnit, label, customerId }: GetOrderStatus,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_OrderStatus_Order> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/orders/status`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetTrackAndTraceOrderStatus = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Gets the order status for existing customers (Track and Trace)
 *
 * @returns ResponseModels_Products_OrderStatus_TrackAndTrace_TrackAndTraceOrderResponseModel Success
 */
export function getTrackAndTraceOrderStatus(
  { businessUnit, label, customerId, accountId }: GetTrackAndTraceOrderStatus,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_OrderStatus_TrackAndTrace_TrackAndTraceOrderResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/orders/trackandtrace`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetMaintenanceDetailsByAgreement = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  agreementId: number;
};
/**
 * Returns the maintenance details for the service products if the customer has a KetelComfort or other service products.
 *
 * @returns ResponseModels_Products_Maintenance_ServiceProductMaintenanceDetailsResponseModel Success
 */
export function getMaintenanceDetailsByAgreement(
  { businessUnit, label, customerId, accountId, agreementId }: GetMaintenanceDetailsByAgreement,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_Maintenance_ServiceProductMaintenanceDetailsResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/products/maintenancedetails/${agreementId}`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetContractExtension = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  agreementIds?: number;
};
/**
 * Returns the contract extension offer for a customer
 *
 * @returns ResponseModels_Products_Offers_PublicOffer Success
 */
export function getContractExtension(
  { businessUnit, label, customerId, accountId, agreementIds }: GetContractExtension,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_Offers_PublicOffer> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/contractextension`,
      query: { agreementIds },
      errors: { 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetContractExtensionV3 = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  agreementIds?: number;
};
/**
 * Returns the contract extension offer for a customer
 *
 * @returns ResponseModels_Products_Offers_V3_PersonalOfferResponseModel Success
 */
export function getContractExtensionV3(
  { businessUnit, label, customerId, accountId, agreementIds }: GetContractExtensionV3,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_Offers_V3_PersonalOfferResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/v3/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/contractextension`,
      query: { agreementIds },
      errors: { 404: 'Not Found' },
    },
    requestConfig,
  );
}

type PutProductsOrderForCustomer = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody: RequestModels_Products_Order_ProductOrderPersonal;
};
/**
 * Create Order For Customer
 *
 * @returns ResponseDataSystem_String Success
 */
export function putProductsOrderForCustomer(
  { businessUnit, label, customerId, accountId, requestBody }: PutProductsOrderForCustomer,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseDataSystem_String> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/products/orders`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type PutProductsOrderForCustomerV2 = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody: RequestModels_Products_Order_ProductOrderPersonal;
};
/**
 * Create Order For Customer
 *
 * @returns ResponseModels_Products_Orders_CreateOrderResponseLocal Success
 */
export function putProductsOrderForCustomerV2(
  { businessUnit, label, customerId, accountId, requestBody }: PutProductsOrderForCustomerV2,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_Orders_CreateOrderResponseLocal> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/v2/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/products/orders`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type CalculateFineCancelledCustomerProducts = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody: RequestModels_Products_FineCalculation_CustomerProductFineCalculationRequestModel;
};
/**
 * Calculates the fine cancelled customer products.
 *
 * @returns ResponseModels_Products_FineCalculation_CustomerProductFineCalculationResponseModel Success
 */
export function calculateFineCancelledCustomerProducts(
  { businessUnit, label, customerId, accountId, requestBody }: CalculateFineCancelledCustomerProducts,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_FineCalculation_CustomerProductFineCalculationResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/products/fine`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetCustomerAccountProductsStatus = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Gets the customerAccount products and their contract status.
 *
 * @returns ResponseModels_Products_ProductsStatusOverviewResponseModel Success
 */
export function getCustomerAccountProductsStatus(
  { businessUnit, label, customerId, accountId }: GetCustomerAccountProductsStatus,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_ProductsStatusOverviewResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/products/status`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type PutDiscontinueConfirmation = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  productType: Products_Relocations_DiscontinueProductType;
  requestBody?: RequestModels_Products_FineCalculation_DiscontinueConfirmation;
};
/**
 * Puts the discontinue confirmation.
 *
 * @returns ResponseDataSystem_String Success
 */
export function putDiscontinueConfirmation(
  { businessUnit, label, customerId, accountId, productType, requestBody }: PutDiscontinueConfirmation,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseDataSystem_String> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/products/${productType}/discontinue`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetDiscontinueIntake = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  productType: Products_Relocations_DiscontinueProductType;
};
/**
 * Get the discontinue intake
 *
 * @returns ResponseModels_Products_FineCalculation_DiscontinueIntakeModel Success
 */
export function getDiscontinueIntake(
  { businessUnit, label, customerId, productType }: GetDiscontinueIntake,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Products_FineCalculation_DiscontinueIntakeModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/products/${productType}/discontinue/intake`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type PutRelocationsCancelation = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody?: RequestModels_Products_Relocations_RelocationCancellationRequestModel;
};
/**
 * MoveOut flow using Relocation, Move out
Create new relocation for customer moving to a Retirement Home or Moving abroad
 * 
 * @returns void 
 */
export function putRelocationsCancelation(
  { businessUnit, label, customerId, accountId, requestBody }: PutRelocationsCancelation,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/relocations/moveout`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetRelocationCancellationIntake = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  relocationReason?: DC_Domain_Models_Customers_RelocationReason;
};
/**
 * Check if this account is eligible for a relocation MoveOut
 *
 * @returns ResponseModels_Customers_Relocations_RelocationIntakeResponse Success
 */
export function getRelocationCancellationIntake(
  { businessUnit, label, customerId, accountId, relocationReason }: GetRelocationCancellationIntake,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Customers_Relocations_RelocationIntakeResponse> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/relocations/moveout/intake`,
      query: { relocationReason },
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}
